import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { User } from '../../../models/user/User.model';
import { DunaValidationStatus } from '../../../dto/users/user-status.dto';
import { EducationalPathUserDTO } from '../../../dto/educational-path/educational-path-user.dto';
import { DunaProfileImageComponent } from '../duna-profile-image/duna-profile-image.component';
import { DunaEducationalPathBadgeComponent } from '../duna-educational-path-badge/duna-educational-path-badge.component';
import { DunaValidationBadgeComponent } from '../duna-validation-badge/duna-validation-badge.component';
import { DunaUserProgressLabelComponent } from '../duna-user-progress-label/duna-user-progress-label.component';
import { buildProgressLabelInputsFromStatus } from '../duna-user-progress-label/duna-user-progress-breakdown.util';
import {
  DirectorySortColumn,
  DirectorySortDirection,
  buildServerLoadParams,
  mapUserListSortToApi,
  toggleDirectorySort,
} from '../duna-directory-list/duna-directory-list.logic';

interface UserListRow {
  user: User;
  uuid: string;
  userId: number | undefined;
  initials: string;
  profileImageObfuscatedName: string | null;
  primaryLocation: string;
  educationalPaths: EducationalPathUserDTO[];
  validationStatus: DunaValidationStatus | null;
  registeredPercentage: number;
  progressPercentage: number;
  completedBarWidth: number;
  intendedBarWidth: number;
  progressLabel: ReturnType<typeof buildProgressLabelInputsFromStatus>;
}

const EMPTY_PATHS: EducationalPathUserDTO[] = [];

@Component({
  selector: 'app-duna-list-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    MatPaginatorModule,
    MatTooltipModule,
    DunaProfileImageComponent,
    DunaEducationalPathBadgeComponent,
    DunaValidationBadgeComponent,
    DunaUserProgressLabelComponent,
  ],
  templateUrl: './duna-list-users.component.html',
  styleUrl: './duna-list-users.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaListUsersComponent implements OnInit, OnDestroy {
  readonly currentYear = new Date().getFullYear();
  readonly pageSizeOptions = [10, 25, 50, 100];

  userSearch = '';
  loading = false;
  loadError: string | null = null;

  pageIndex = 0;
  pageSize = 25;
  totalCount = 0;

  sortColumn: DirectorySortColumn = 'progress';
  sortDirection: DirectorySortDirection = 'desc';

  displayedRows: UserListRow[] = [];

  private readonly destroy$ = new Subject<void>();
  private readonly searchInput$ = new Subject<string>();

  constructor(
    private dataSourceService: ComponentDataSourceService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadUsers();
      });
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUserSearchChange(value: string): void {
    this.userSearch = value;
    this.searchInput$.next(value);
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadUsers();
  }

  onSortHeaderClick(column: DirectorySortColumn, event: Event): void {
    event.stopPropagation();
    const next = toggleDirectorySort(
      this.sortColumn,
      this.sortDirection,
      column
    );
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.pageIndex = 0;
    this.loadUsers();
  }

  sortIcon(column: DirectorySortColumn): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getCountLabel(): string {
    return `${this.totalCount}`;
  }

  onUserRowClick(row: UserListRow): void {
    if (!row.uuid) {
      return;
    }
    this.router.navigate(['/profile', row.uuid]);
  }

  private loadUsers(): void {
    this.loading = true;
    this.loadError = null;

    this.dataSourceService
      .loadDataWithFilters(
        'brugere_status',
        buildServerLoadParams(
          'brugere_status',
          this.pageIndex,
          this.pageSize,
          this.userSearch,
          this.sortColumn,
          this.sortDirection,
          mapUserListSortToApi
        )
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const items = (result?.items ?? []) as User[];
          this.displayedRows = items.map((user) => this.buildUserRow(user));
          this.totalCount = result?.totalCount ?? items.length;
          this.pageIndex = result?.pageIndex ?? this.pageIndex;
          this.pageSize = result?.pageSize ?? this.pageSize;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.displayedRows = [];
          this.totalCount = 0;
          this.loading = false;
          this.loadError = 'Kunne ikke indlæse brugere. Prøv igen senere.';
          this.cdr.markForCheck();
        },
      });
  }

  private buildUserRow(user: User): UserListRow {
    const raw = user as User & {
      profile_image_obfuscated_name?: string | null;
      id?: number | null;
    };
    const progressLabel = buildProgressLabelInputsFromStatus(user.status);
    return {
      user,
      uuid: user.uuid,
      userId: raw.id ?? undefined,
      initials: this.getUserInitials(user),
      profileImageObfuscatedName: raw.profile_image_obfuscated_name ?? null,
      primaryLocation: user.primaryLocation,
      educationalPaths: progressLabel.paths,
      validationStatus: this.getValidationStatus(user),
      registeredPercentage: progressLabel.registeredPercentage,
      progressPercentage: progressLabel.progressPercentage,
      completedBarWidth: progressLabel.progressPercentage,
      intendedBarWidth: Math.max(
        0,
        progressLabel.registeredPercentage - progressLabel.progressPercentage
      ),
      progressLabel,
    };
  }

  private getUserInitials(user: User): string {
    const first = user.first_name?.charAt(0) ?? '';
    const last = user.last_name?.charAt(0) ?? '';
    const initials = `${first}${last}`.trim();
    return initials || '?';
  }

  private getValidationStatus(user: User): DunaValidationStatus | null {
    const status = user.validation_status as string | undefined;
    if (!status || status === 'hidden') {
      return 'no_data';
    }
    if (
      status === 'valid' ||
      status === 'attention' ||
      status === 'no_data' ||
      status === 'no_paths'
    ) {
      return status;
    }
    return 'no_data';
  }

  private clampPercent(value: number | undefined | null): number {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return 0;
    }
    return Math.min(100, Math.max(0, Number(value)));
  }
}
